import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getMessages } from '@/api/messages';
import { mergeMessageIntoCache } from './useMessages';
import { showWarning } from '@/lib/toast';

const INITIAL_INTERVAL = 2000; // 2 seconds
const MAX_INTERVAL = 30000; // 30 seconds
const BACKOFF_MULTIPLIER = 1.5;
const ERROR_BACKOFF_MULTIPLIER = 2;
const ERROR_WARNING_THRESHOLD = 3;

/**
 * Hook to poll for new messages when realtime is not connected
 *
 * Features:
 * - Uses ISO timestamp (created_at), not message ID
 * - Exponential backoff on inactivity (2s → 30s)
 * - Resets to 2s on new activity
 * - Merges new messages into cache without full refetch
 * - Shows warning after consecutive errors
 *
 * @param conversationId - The conversation to poll
 * @param enabled - Whether polling should be active (typically !realtimeConnected)
 */
export function usePollingFallback(conversationId: string, enabled: boolean) {
  const queryClient = useQueryClient();

  // Use ISO timestamp for the `since` parameter (not message ID)
  const lastTimestampRef = useRef<string | null>(null);
  const intervalRef = useRef(INITIAL_INTERVAL);
  const timeoutIdRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorCountRef = useRef(0);

  useEffect(() => {
    if (!enabled || !conversationId) {
      // Clear any pending timeout when disabled
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
      return;
    }

    const poll = async () => {
      try {
        const data = await getMessages(
          lastTimestampRef.current
            ? { conversationId, since: lastTimestampRef.current, limit: 50 }
            : { conversationId, limit: 50 }
        );

        // Reset error count on success
        errorCountRef.current = 0;

        if (data.messages.length > 0) {
          // Track last message's created_at as ISO timestamp
          // Use non-null assertion since we checked length > 0
          lastTimestampRef.current = data.messages[data.messages.length - 1]!.created_at;

          // Reset interval on activity
          intervalRef.current = INITIAL_INTERVAL;

          // Merge each new message into cache
          data.messages.forEach((msg) => {
            mergeMessageIntoCache(queryClient, conversationId, msg);
          });
        } else {
          // Exponential backoff on no activity
          intervalRef.current = Math.min(
            intervalRef.current * BACKOFF_MULTIPLIER,
            MAX_INTERVAL
          );
        }
      } catch {
        // Increment error count and show warning after threshold
        errorCountRef.current++;
        if (errorCountRef.current === ERROR_WARNING_THRESHOLD) {
          showWarning('Having trouble connecting. Messages may be delayed.');
        }

        // On error, slow down polling more aggressively
        intervalRef.current = Math.min(
          intervalRef.current * ERROR_BACKOFF_MULTIPLIER,
          MAX_INTERVAL
        );
      }

      // Schedule next poll
      timeoutIdRef.current = setTimeout(poll, intervalRef.current);
    };

    // Start polling
    timeoutIdRef.current = setTimeout(poll, intervalRef.current);

    // Cleanup
    return () => {
      if (timeoutIdRef.current) {
        clearTimeout(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, [conversationId, enabled, queryClient]);

  // Reset state when conversation changes
  useEffect(() => {
    lastTimestampRef.current = null;
    intervalRef.current = INITIAL_INTERVAL;
    errorCountRef.current = 0;
  }, [conversationId]);
}
